import {
  Application,
  Container,
  Graphics,
  Sprite,
  Texture,
  Assets,
  Text,
  TextStyle,
} from 'pixi.js';
import { Card, GameState, Player, PlayedCard, Trick } from '../engine/types';
import { CardSprite } from './cardSprite';
import { COLORS, CARD_WIDTH, CARD_HEIGHT } from './constants';
import { playCardSnap, playCorvenSlay } from '../audio';

export interface BoardCallbacks {
  onCardPlayed: (card: Card) => void;
}

export class BoardRenderer {
  public app: Application;
  private rootContainer: Container;
  private matGfx: Graphics;
  private trickContainer: Container;
  private localHandContainer: Container;
  private opponentContainers: Map<string, Container> = new Map();
  private effectsContainer: Container;

  private cardTextures: Map<string, Texture> = new Map();
  private backTexture: Texture | null = null;

  private localPlayerId: string = '';
  private currentGameState: GameState | null = null;
  private callbacks: BoardCallbacks;

  private isInitialized: boolean = false;
  private heldTrickDisplay: Trick | null = null;

  constructor(callbacks: BoardCallbacks) {
    this.callbacks = callbacks;
    this.app = new Application();
    this.rootContainer = new Container();
    this.matGfx = new Graphics();
    this.trickContainer = new Container();
    this.localHandContainer = new Container();
    this.effectsContainer = new Container();
  }

  public async init(containerElement: HTMLElement): Promise<void> {
    await this.app.init({
      resizeTo: containerElement,
      backgroundColor: COLORS.bgLight,
      antialias: true,
      resolution: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
      autoDensity: true,
    });

    containerElement.appendChild(this.app.canvas);

    this.app.stage.addChild(this.rootContainer);
    this.rootContainer.addChild(this.matGfx);
    this.rootContainer.addChild(this.trickContainer);
    this.rootContainer.addChild(this.localHandContainer);
    this.rootContainer.addChild(this.effectsContainer);

    await this.loadTextures();
    this.isInitialized = true;

    this.onResize();
    window.addEventListener('resize', () => this.onResize());
  }

  private async loadTextures(): Promise<void> {
    try {
      this.backTexture = await Assets.load('./assets/Back.jpg');

      // Load all 27 card textures
      const loadPromises: Promise<void>[] = [];
      for (let i = 1; i <= 27; i++) {
        const numStr = i < 10 ? `0${i}` : `${i}`;
        const path = `./assets/cards/card_${numStr}.jpg`;
        const p = Assets.load(path)
          .then((tex: Texture) => {
            this.cardTextures.set(`card_${numStr}`, tex);
          })
          .catch((err) => {
            console.warn(`Could not load texture: ${path}`, err);
          });
        loadPromises.push(p);
      }
      await Promise.all(loadPromises);
    } catch (err) {
      console.warn('Assets loading exception:', err);
    }

    if (!this.backTexture) {
      this.backTexture = Texture.WHITE;
    }
  }

  public setLocalPlayer(playerId: string): void {
    this.localPlayerId = playerId;
  }

  public updateState(state: GameState): void {
    this.currentGameState = state;
    if (!this.isInitialized) return;

    this.drawFeltMat();
    this.renderLocalHand();
    this.renderOpponentHands();
    this.renderTrickCards();

    // Trigger Corven Slay visual effect if active
    if (state.currentTrick.corvenSlayWinnerId) {
      this.triggerCorvenSlayEffect();
    }
  }

  public showTrickResolution(trick: Trick): void {
    this.heldTrickDisplay = trick;
    this.renderTrickCards();
    this.renderLocalHand();
  }

  public clearTrickResolution(): void {
    this.heldTrickDisplay = null;
    this.renderTrickCards();
    this.renderLocalHand();
  }

  private onResize(): void {
    if (!this.app.renderer) return;
    this.drawFeltMat();
    if (this.currentGameState) {
      this.updateState(this.currentGameState);
    }
  }

  public getResponsiveCardDimensions(): { width: number; height: number; scale: number } {
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;

    const baseW = CARD_WIDTH; // 135

    if (screenW < 520) {
      // Mobile portrait
      const targetW = Math.max(94, Math.min(120, (screenW - 40) / 3.2));
      const targetH = targetW * 1.4; // 2.5 : 3.5 = 1 : 1.4
      return { width: targetW, height: targetH, scale: targetW / baseW };
    } else if (screenW < 800) {
      // Tablet / small screens
      const targetW = Math.max(115, Math.min(142, (screenW - 70) / 3.3));
      const targetH = targetW * 1.4;
      return { width: targetW, height: targetH, scale: targetW / baseW };
    } else if (screenH < 720) {
      // Laptop / low height
      const targetH = Math.min(195, screenH * 0.28);
      const targetW = targetH / 1.4;
      return { width: targetW, height: targetH, scale: targetW / baseW };
    } else {
      // Standard / Large desktop
      const targetW = Math.min(168, Math.max(138, screenW * 0.11));
      const targetH = targetW * 1.4;
      return { width: targetW, height: targetH, scale: targetW / baseW };
    }
  }

  private drawFeltMat(): void {
    const w = this.app.screen.width;
    const h = this.app.screen.height;

    this.matGfx.clear();

    // Table mat dimensions (organic curved rectangle with subtle border)
    const marginX = Math.max(12, Math.min(40, w * 0.03));
    const marginTop = Math.max(14, Math.min(28, h * 0.03));
    const marginBottom = Math.max(14, Math.min(28, h * 0.03));
    const matW = w - marginX * 2;
    const matH = h - marginTop - marginBottom;
    const cornerRadius = Math.min(32, Math.min(matW, matH) * 0.08);

    // Outer subtle felt border
    this.matGfx.roundRect(marginX, marginTop, matW, matH, cornerRadius);
    this.matGfx.fill({ color: COLORS.feltMat, alpha: 0.98 });
    this.matGfx.stroke({ width: 3, color: COLORS.feltBorder, alpha: 0.8 });

    // Inner subtle stitching line
    this.matGfx.roundRect(marginX + 6, marginTop + 6, matW - 12, matH - 12, cornerRadius - 4);
    this.matGfx.stroke({ width: 1.5, color: COLORS.feltBorder, alpha: 0.4 });

    // Center circular table felt arena
    const cx = w / 2;
    const cy = h / 2 - 12;
    const arenaRadius = Math.min(190, Math.min(matW, matH) * 0.28);
    this.matGfx.circle(cx, cy, arenaRadius);
    this.matGfx.fill({ color: 0xECE6DC, alpha: 0.35 });
    this.matGfx.stroke({ width: 2, color: COLORS.feltBorder, alpha: 0.45 });
  }

  private renderLocalHand(): void {
    this.localHandContainer.removeChildren();
    if (!this.currentGameState) return;

    const localPlayer = this.currentGameState.players.find((p) => p.id === this.localPlayerId);
    if (!localPlayer || localPlayer.hand.length === 0) return;

    const dim = this.getResponsiveCardDimensions();
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;

    const handY = screenH - dim.height / 2 - 16;
    const cardSpacing = Math.min(dim.width + 16, (screenW - 50) / localPlayer.hand.length);
    const startX = screenW / 2 - ((localPlayer.hand.length - 1) * cardSpacing) / 2;

    const isMyTurn =
      this.currentGameState.status === 'playing' &&
      this.currentGameState.currentTurnPlayerId === this.localPlayerId &&
      this.currentGameState.challenge.status !== 'pending' &&
      !localPlayer.folded &&
      !this.heldTrickDisplay;

    localPlayer.hand.forEach((card, index) => {
      const cardX = startX + index * cardSpacing;
      const cardTexture = this.getCardTexture(card);
      const sprite = new CardSprite(cardTexture, this.backTexture || Texture.WHITE);

      sprite.scale.set(dim.scale);
      sprite.setCard(card, cardTexture);
      sprite.setFaceUp(true);
      sprite.x = cardX;
      sprite.resetPosition(handY);
      sprite.setPlayable(isMyTurn);

      sprite.onCardPlayed = (c) => {
        playCardSnap();
        this.callbacks.onCardPlayed(c);
      };
      sprite.onCardClicked = (c) => {
        playCardSnap();
        this.callbacks.onCardPlayed(c);
      };

      this.localHandContainer.addChild(sprite);
    });
  }

  private renderOpponentHands(): void {
    if (!this.currentGameState) return;

    const dim = this.getResponsiveCardDimensions();
    const opponents = this.currentGameState.players.filter((p) => p.id !== this.localPlayerId);
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;

    // Remove obsolete containers
    for (const [id, cont] of this.opponentContainers) {
      if (!opponents.some((o) => o.id === id)) {
        this.rootContainer.removeChild(cont);
        this.opponentContainers.delete(id);
      }
    }

    const oppTopY = Math.max(90, Math.min(120, screenH * 0.16));

    if (opponents.length === 1) {
      // 1v1 Duel: Single opponent placed at North (top center)
      const opp = opponents[0];
      let cont = this.opponentContainers.get(opp.id);
      if (!cont) {
        cont = new Container();
        this.opponentContainers.set(opp.id, cont);
        this.rootContainer.addChild(cont);
      }
      this.renderHandStrip(cont, opp, screenW / 2, oppTopY, 0, dim.scale * 0.72);
    } else {
      // 2v2 or 4P FFA: 3 opponents placed at West (Left), North (Top), East (Right)
      const positions = [
        { x: Math.max(70, screenW * 0.08), y: screenH / 2 - 12, rot: Math.PI / 2 }, // Left (West)
        { x: screenW / 2, y: oppTopY, rot: 0 }, // Top (North)
        { x: Math.min(screenW - 70, screenW * 0.92), y: screenH / 2 - 12, rot: -Math.PI / 2 }, // Right (East)
      ];

      opponents.forEach((opp, i) => {
        const pos = positions[i % positions.length];
        let cont = this.opponentContainers.get(opp.id);
        if (!cont) {
          cont = new Container();
          this.opponentContainers.set(opp.id, cont);
          this.rootContainer.addChild(cont);
        }
        this.renderHandStrip(cont, opp, pos.x, pos.y, pos.rot, dim.scale * 0.65);
      });
    }
  }

  private renderHandStrip(
    cont: Container,
    player: Player,
    centerX: number,
    centerY: number,
    rotation: number,
    scale: number
  ): void {
    cont.removeChildren();
    cont.x = centerX;
    cont.y = centerY;
    cont.rotation = rotation;

    const count = player.hand.length;
    if (count === 0) return;

    const spacing = Math.min(32, 24 * (scale / 0.7));
    const startX = -((count - 1) * spacing) / 2;

    for (let i = 0; i < count; i++) {
      const s = new Sprite(this.backTexture || Texture.WHITE);
      s.width = CARD_WIDTH * scale;
      s.height = CARD_HEIGHT * scale;
      s.anchor.set(0.5);
      s.x = startX + i * spacing;
      s.y = 0;
      cont.addChild(s);
    }
  }

  private renderTrickCards(): void {
    this.trickContainer.removeChildren();
    if (!this.currentGameState) return;

    const trick = this.heldTrickDisplay || this.currentGameState.currentTrick;
    if (!trick || trick.plays.length === 0) return;

    const dim = this.getResponsiveCardDimensions();
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;
    const centerX = screenW / 2;
    const centerY = screenH / 2 - 12;

    const plays = trick.plays;
    const count = plays.length;

    // Evaluate trick leader/winner to satisfy Requirement 10:
    // "O player que está ganhando a carta fica por cima (maior z-index / no topo da pilha)"
    let maxPower = -1;
    plays.forEach((p) => {
      if (p.card.power > maxPower) maxPower = p.card.power;
    });

    // Identify who is actually winning the trick (avoiding false winner highlights on ties)
    let currentWinnerId: string | null = trick.winnerId;
    if (!currentWinnerId && !trick.isTied) {
      const ezarionSlay = plays.find((p) => p.slayedEzarion);
      if (ezarionSlay) {
        currentWinnerId = ezarionSlay.playerId;
      } else {
        const highestPlays = plays.filter((p) => p.card.power === maxPower);
        if (highestPlays.length === 1) {
          currentWinnerId = highestPlays[0].playerId;
        } else if (highestPlays.length > 1 && this.currentGameState?.mode === 'teams') {
          const team = this.currentGameState.players.find((p) => p.id === highestPlays[0].playerId)?.team;
          const allSameTeam = highestPlays.every(
            (hp) => this.currentGameState?.players.find((p) => p.id === hp.playerId)?.team === team
          );
          if (allSameTeam) {
            currentWinnerId = highestPlays[0].playerId;
          }
        }
      }
    }

    // Calculate layout coordinates for each play
    const trickScale = dim.scale * 0.90;
    const cardW = CARD_WIDTH * trickScale;
    const cardH = CARD_HEIGHT * trickScale;
    const radius = count > 2 ? Math.max(70, cardW * 0.70) : cardW * 0.40;

    interface PlayRenderItem {
      play: PlayedCard;
      idx: number;
      x: number;
      y: number;
      rotation: number;
      isWinning: boolean;
    }

    const items: PlayRenderItem[] = plays.map((play, idx) => {
      let x = centerX;
      let y = centerY;
      if (count === 2) {
        x = centerX + (idx === 0 ? -cardW * 0.45 : cardW * 0.45);
        y = centerY;
      } else {
        const angle = (idx / count) * Math.PI * 2 - Math.PI / 2;
        x = centerX + Math.cos(angle) * radius;
        y = centerY + Math.sin(angle) * radius;
      }

      const rotation = ((idx % 3) - 1) * 0.05;
      const isWinning = currentWinnerId !== null && play.playerId === currentWinnerId;

      return { play, idx, x, y, rotation, isWinning };
    });

    // Requirement 10: Sort items so the winning card is added LAST (rendered on top!)
    items.sort((a, b) => {
      if (a.isWinning === b.isWinning) return a.idx - b.idx;
      return a.isWinning ? 1 : -1;
    });

    items.forEach((item) => {
      const play = item.play;
      const cardTex = this.getCardTexture(play.card);

      // Container for card + highlight + badge
      const cardContainer = new Container();
      cardContainer.x = item.x;
      cardContainer.y = item.y;
      cardContainer.rotation = item.rotation;

      // Golden Halo glow if winning
      if (item.isWinning) {
        const glowGfx = new Graphics();
        glowGfx.roundRect(-cardW / 2 - 6, -cardH / 2 - 6, cardW + 12, cardH + 12, 12);
        glowGfx.fill({ color: COLORS.turnHighlight, alpha: 0.65 });
        glowGfx.stroke({ width: 3, color: COLORS.accentGold, alpha: 0.95 });
        cardContainer.addChild(glowGfx);
      }

      const sprite = new Sprite(cardTex);
      sprite.width = cardW;
      sprite.height = cardH;
      sprite.anchor.set(0.5);
      cardContainer.addChild(sprite);

      // Card border
      const borderGfx = new Graphics();
      borderGfx.roundRect(-cardW / 2, -cardH / 2, cardW, cardH, 8);
      borderGfx.stroke({
        width: item.isWinning ? 2.5 : 1.5,
        color: item.isWinning ? COLORS.accentGold : COLORS.cardBorder,
        alpha: 0.9,
      });
      cardContainer.addChild(borderGfx);

      // Player name badge above card
      const player = this.currentGameState!.players.find((p) => p.id === play.playerId);
      if (player) {
        const badgeGfx = new Graphics();
        const badgeTitle = player.name;
        const badgeText = new Text({
          text: badgeTitle,
          style: new TextStyle({
            fontSize: item.isWinning ? 12 : 11,
            fontWeight: item.isWinning ? '800' : 'bold',
            fill: item.isWinning ? 0x92400E : COLORS.textDark,
          }),
        });
        badgeText.anchor.set(0.5);
        badgeText.x = 0;
        badgeText.y = -cardH / 2 - 14;

        badgeGfx.roundRect(
          badgeText.x - badgeText.width / 2 - 8,
          badgeText.y - 10,
          badgeText.width + 16,
          20,
          8
        );
        badgeGfx.fill({
          color: item.isWinning ? 0xFEF3C7 : 0xFFFFFF,
          alpha: 0.95,
        });
        badgeGfx.stroke({
          width: 1.5,
          color: item.isWinning ? COLORS.accentGold : COLORS.feltBorder,
          alpha: 0.9,
        });

        cardContainer.addChild(badgeGfx);
        cardContainer.addChild(badgeText);
      }

      this.trickContainer.addChild(cardContainer);
    });
  }

  private triggerCorvenSlayEffect(): void {
    playCorvenSlay();

    const slashGfx = new Graphics();
    this.effectsContainer.addChild(slashGfx);

    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;
    const cx = screenW / 2;
    const cy = screenH / 2 - 12;

    let progress = 0;
    const animate = () => {
      progress += 0.05;
      slashGfx.clear();

      // Slashing red sword beam
      slashGfx.moveTo(cx - 140 * progress, cy - 90 * progress);
      slashGfx.lineTo(cx + 140 * progress, cy + 90 * progress);
      slashGfx.stroke({
        width: 10 * (1 - progress * 0.5),
        color: COLORS.corvenRed,
        alpha: 1 - progress,
      });

      // Spark glow
      slashGfx.circle(cx, cy, 35 * progress);
      slashGfx.fill({ color: 0xFFFFFF, alpha: 0.55 * (1 - progress) });

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.effectsContainer.removeChild(slashGfx);
      }
    };
    requestAnimationFrame(animate);
  }

  private getCardTexture(card: Card): Texture {
    const match = card.image.match(/card_(\d+)\.jpg/);
    if (match) {
      const key = `card_${match[1]}`;
      const tex = this.cardTextures.get(key);
      if (tex) return tex;
    }
    return Texture.WHITE;
  }

  public destroy(): void {
    this.app.destroy(true, { children: true, texture: false });
  }
}
